import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';
const API_KEY = process.env.REACT_APP_API_KEY || '';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  },
  timeout: 10000,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Always ensure x-api-key header is present
    if (API_KEY && !config.headers['x-api-key']) {
      config.headers['x-api-key'] = API_KEY;
    }
    
    // Don't auto-add accessToken for endpoints that use URL token
    const urlTokenEndpoints = [
      '/auth/admin/reset-password',
      '/auth/admin/set-password',
    ];
    
    const isUrlTokenEndpoint = urlTokenEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    // Only add accessToken from localStorage if NOT a URL token endpoint
    // AND no Authorization header already set
    if (!isUrlTokenEndpoint && !config.headers.Authorization) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Add /admins prefix to all authenticated endpoints (except auth endpoints)
    // Auth endpoints already have /auth/admins prefix, so we skip those
    if (config.url && !config.url.startsWith('/auth')) {
      // Only add /admins prefix if it's not already there
      if (!config.url.startsWith('/admins')) {
        config.url = `/admins${config.url}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && originalRequest) {
      const requestUrl = originalRequest.url || '';
      
      console.error(`🚫 401 Unauthorized error for ${requestUrl}`);
      console.error('Response:', error.response?.data);
      
      // Don't redirect if user is already on auth page or if it's a login/auth request
      const isAuthRequest = 
        requestUrl.includes('/auth/admin/login') ||
        requestUrl.includes('/auth/admin/2fa') ||
        requestUrl.includes('/auth/admin/forgot-password') ||
        requestUrl.includes('/auth/admin/reset-password') ||
        requestUrl.includes('/auth/admin/set-password') ||
        requestUrl.includes('/auth/admin/verify') ||
        requestUrl.includes('/auth/admin/verify-reset') ||
        requestUrl.includes('/auth/admin/confirm-reset-code');
      
      const isOnAuthPage = window.location.pathname === '/auth' || 
                           window.location.pathname.startsWith('/auth?');

      // Only clear tokens and redirect if this is NOT an auth request
      // and user is NOT already on the auth page
      if (!isAuthRequest && !isOnAuthPage) {
        console.warn('🔄 Redirecting to sign-in due to 401 error');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userEmail');
        window.location.href = '/auth';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

